/**
 * Renders one scraper-supplied value as display text.
 *
 * Everything here arrives as `unknown` off chapter sites the scraper does not
 * control, so the output is always a plain string destined for a React text
 * node. It must never be handed to `Paragraph`'s `html` prop or any other
 * `dangerouslySetInnerHTML` path.
 */
export const formatScrapeReviewValue = (value: unknown): string => {
  // These three render differently on purpose. Collapsing them all to one dash
  // makes a real change read as a no-op: a diff row of `{ before: null,
  // after: '' }` would display "— → —" under a heading that says "What would
  // change", and a reviewer would approve it as harmless.
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.trim() === '' ? '(empty)' : value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length
      ? value.map(formatScrapeReviewValue).join(', ')
      : '(none)';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      // Circular, or something else JSON refuses. A row that says so beats a
      // detail pane that throws while an admin is reading it.
      return '[unserializable]';
    }
  }
  return String(value);
};

/**
 * Own-property check, used everywhere this screen looks a document-controlled
 * string up in a map.
 *
 * NOT `Object.hasOwn`, which is ES2022. This repo declares no `browserslist`,
 * so Next's default target applies — chrome 64 / edge 79 / firefox 67 / opera
 * 51 / safari 12, every one of which predates it — and Next's legacy polyfill
 * does not shim it. `Object.hasOwn` would therefore throw on the first render
 * for EVERY document on an in-baseline browser: a total outage of the screen,
 * strictly worse than the single-hostile-document crash these guards exist to
 * prevent. `hasOwnProperty.call` is ES3 and identical here.
 */
export const hasOwnKey = (target: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(target, key);

/**
 * Whether a value is a shape this screen can read as a record of fields.
 *
 * Absent counts as readable — an `add` proposal legitimately has an empty
 * diff. A primitive or an array does not: the formatters return no rows for
 * it, which is indistinguishable from an empty record, and the pane turns "no
 * rows" into a positive claim ("the scraper recorded no field-level changes").
 * Saying that about data we could not read is the misrepresentation this
 * screen exists to prevent.
 */
export const isReadableRecord = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'object' && !Array.isArray(value));

/** `existingMeetingId` -> `Existing meeting id`. Used for keys we have no label for. */
export const humanizeFieldKey = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};
